import React, { useEffect, useState } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
  ExpressCheckoutElement,
} from "@stripe/react-stripe-js";
import { toast } from "react-hot-toast";
import { CreditCard } from "lucide-react";
import { BASE_URL } from "../utils/tools";
import { Spinner } from "@jammable/ui-components/Loader";

// intentType "setup" confirms a SetupIntent (free-trial checkout: card saved
// now, first charge after the trial) instead of paying a PaymentIntent.
// `express` puts Apple Pay and Google Pay buttons above the card fields (and
// takes them out of the card form's tabs), for people coming back after a
// card didn't go through or they left checkout (UpgradeModal).
const StripePaymentElement = ({
  price,
  submitLabel,
  // Optional layout hooks (the full-screen paywall pins the button to the
  // bottom of the screen with its terms under it).
  formClassName = "",
  submitWrapClassName = "",
  footer = null,
  onReady,
  discount,
  currency,
  intentType = "payment",
  express = false,
}) => {
  const stripe = useStripe();
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);
  const [walletsShown, setWalletsShown] = useState(false);
  const elements = useElements();

  useEffect(() => {
    if (elements) {
      const element = elements.getElement("payment");
      element.on("ready", () => {});
      element.on("change", () => {
        setError(null);
      });
    }
  }, [elements]);

  const confirm = () => {
    const confirmFn =
      intentType === "setup"
        ? stripe.confirmSetup.bind(stripe)
        : stripe.confirmPayment.bind(stripe);
    return confirmFn({
      elements,
      confirmParams: {
        return_url: `${BASE_URL}/thankyou${intentType === "setup" ? "?trial=1" : ""}`,
      },
    });
  };

  // Apple Pay / Google Pay: the wallet's sheet has already taken the payment
  // details; this confirms the same intent the card form would.
  async function handleExpressConfirm(event) {
    if (!stripe || !elements) return;
    const { error: e } = await confirm();
    if (e) {
      event.paymentFailed?.({ reason: "fail" });
      setError(e.message);
    }
  }

  async function handleCheckoutFormSubmit(event) {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }
    setLoading(true);

    const stripePayload = await confirm();
    setLoading(false);
    if (stripePayload.error) {
      setLoading(false);
      // dispatch(actions.errorGlobal(stripePayload.error.message));
      if (stripePayload?.error?.message === "Your card has been declined.") {
        setError(
          "Your card has been declined. Please contact your bank or try a different payment method.",
        );
      } else {
        setError(stripePayload?.error?.message);
      }
    } else {
      toast.success(
        intentType === "setup"
          ? "Your free trial has started!"
          : `Thank you for your purchase!`,
      );
    }
    setLoading(false);
  }

  return (
    <form
      style={{ marginBottom: 0 }}
      className={formClassName}
      onSubmit={handleCheckoutFormSubmit}
    >
      {error && (
        <div className="tw-mb-4 tw-rounded-[20px] tw-bg-[#FDECEF] tw-px-4 tw-py-3 tw-text-sm tw-font-bold tw-text-[#B4123A]">
          {error}
        </div>
      )}

      {express && (
        // Hidden until Stripe says a wallet is available on this device.
        <div
          className={
            walletsShown
              ? "tw-mb-4 tw-flex tw-flex-col tw-gap-3"
              : "tw-h-0 tw-overflow-hidden tw-opacity-0"
          }
        >
          <ExpressCheckoutElement
            options={{
              buttonHeight: 52,
              buttonType: { applePay: "plain", googlePay: "plain" },
              paymentMethods: {
                link: "never",
                paypal: "never",
                amazonPay: "never",
              },
            }}
            onReady={({ availablePaymentMethods }) =>
              setWalletsShown(
                !!availablePaymentMethods &&
                  Object.values(availablePaymentMethods).some(Boolean),
              )
            }
            onConfirm={handleExpressConfirm}
          />
          <div className="tw-flex tw-items-center tw-gap-3 tw-text-xs tw-font-bold tw-opacity-70">
            <span className="tw-h-px tw-flex-1 tw-bg-current tw-opacity-30" />
            or pay with card
            <span className="tw-h-px tw-flex-1 tw-bg-current tw-opacity-30" />
          </div>
        </div>
      )}

      <div className="tw-mb-5">
        <PaymentElement
          onReady={onReady}
          options={
            express
              ? { wallets: { applePay: "never", googlePay: "never" } }
              : undefined
          }
        />
      </div>

      <div className={submitWrapClassName}>
        {/* Design language F primary CTA: lime pill, ink text, no shadow. */}
        <button
          className="tw-flex tw-h-[58px] tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-lime tw-px-4 tw-text-[17px] tw-font-extrabold tw-text-jam-ink tw-transition hover:tw-brightness-95 active:tw-scale-[0.98] disabled:tw-cursor-not-allowed disabled:tw-opacity-50 md:tw-h-[60px]"
          type="submit"
        >
          {loading ? (
            <Spinner className="tw-border-3 tw-border-current" size={4} />
          ) : (
            <div className="tw-flex tw-items-center tw-gap-2">
              <CreditCard size={20} strokeWidth={2.4} />
              <div>
                {submitLabel ||
                  (intentType === "setup" ? "Start free trial" : "Pay")}
              </div>
            </div>
          )}
        </button>
        {footer}
      </div>
    </form>
  );
};

export default StripePaymentElement;
